/**
 * ProcessPlaybackProvider — process playback as a workspace-scoped service.
 *
 * The active process is part of the store SELECTION (picking a process is a
 * selection act, restored by back-nav); everything interactive around it —
 * the step scrubber, auto-play, per-process walker branches/expansions, and
 * the heavy walk orchestration — lives here, shared via context so the
 * scrubber chrome, the board's edge decorations, and the process panels all
 * read ONE controller (mounting it twice would walk the process twice).
 *
 * Process actions dispatch through the store:
 *   - `handleSelectProcess`   — plain toggle/clear, history-neutral.
 *   - `handleActivateProcess` — a drill-down: pushes the prior selection so
 *     back-nav can undo the activation.
 *   - `handleLocateStep`      — resolves the active step's endpoint modules
 *     and emits `framePair`; whatever board is mounted frames them.
 */
import {
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { StableId } from "@archlang/scene/arch-model";
import {
  useProcessPlayback,
  type ProcessScrubberControl,
} from "../hooks/useProcessPlayback.js";
import { findModuleByName } from "../hooks/useModelIndexes.js";
import { useWorkspace } from "./WorkspaceContext.js";
import { useWorkspaceSelection, useWorkspaceProcess } from "./WorkspaceStore.js";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import {
  ProcessPlaybackContext,
  type ProcessPlaybackContextValue,
} from "./ProcessPlaybackContext.js";
import { stepActorId, stepParticipants } from "../process/stepActor.js";

export function ProcessPlaybackProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const store = useWorkspace();
  const { model, resolvedModel } = useWorkspaceModel();
  const selection = useWorkspaceSelection(store);
  const activeProcessId = selection.activeProcessId as StableId | null;

  // Scrubber step + auto-play are CONTROLLED by the per-tab store slice, so
  // they survive a tab switch (each tab restores its own playback position).
  const proc = useWorkspaceProcess(store);
  const scrubber = useMemo<ProcessScrubberControl>(
    () => ({
      stepIndex: proc.stepIndex,
      setStepIndex: (upd) => store.setProcessStep(upd),
      autoPlay: proc.autoPlay,
      setAutoPlay: (upd) =>
        store.setProcessAutoPlay(
          typeof upd === "function" ? upd(store.getState().process.autoPlay) : upd,
        ),
    }),
    [store, proc.stepIndex, proc.autoPlay],
  );
  const playback = useProcessPlayback(model, activeProcessId, scrubber, resolvedModel);

  const handleSelectProcess = useCallback(
    (id: StableId | null) => store.select({ activeProcessId: id }),
    [store],
  );

  // Activate AND push to nav history, so the back nav can undo the
  // activation. Keeps the current module/edge selection.
  const handleActivateProcess = useCallback(
    (id: StableId) => {
      const sel = store.getState().selection;
      store.applySelection(
        { moduleId: sel.moduleId, edgeId: sel.edgeId, activeProcessId: id },
        "sidebar",
      );
    },
    [store],
  );

  const { walkResult, activeStepIndex } = playback;
  const handleLocateStep = useCallback(() => {
    const step = walkResult?.steps.find(
      (st) => st.stepIndex === activeStepIndex,
    );
    if (!step) return;
    // WalkStep endpoints are QualifiedName segment arrays, and a callee
    // usually ends with the INTERFACE segment (`Notifications.sendEmail`) —
    // trim trailing segments until a module matches.
    const resolveStepModule = (qn: readonly string[]): StableId | null => {
      for (let n = qn.length; n >= 1; n--) {
        const m = findModuleByName(
          model?.modules ?? [],
          qn.slice(0, n).join("."),
        );
        if (m?.id) return m.id;
      }
      return null;
    };
    const a = resolveStepModule(step.from);
    const b = resolveStepModule(step.to);
    if (!a || !b) return;
    store.emit({ type: "framePair", aId: a, bId: b });
  }, [store, walkResult, activeStepIndex, model]);

  // Board projections of the active step: the acting node of a
  // self-referential row (ring) and the step's endpoint pair (auto-framing).
  const currentStepActorId = useMemo(
    () => stepActorId(walkResult, activeStepIndex, model?.modules ?? []),
    [walkResult, activeStepIndex, model],
  );
  const currentStepParticipants = useMemo(
    () => stepParticipants(walkResult, activeStepIndex, model?.modules ?? []),
    [walkResult, activeStepIndex, model],
  );

  const value = useMemo<ProcessPlaybackContextValue>(
    () => ({
      ...playback,
      activeProcessId,
      handleSelectProcess,
      handleActivateProcess,
      handleLocateStep,
      stepActorId: currentStepActorId,
      stepParticipants: currentStepParticipants,
    }),
    [
      playback,
      activeProcessId,
      handleSelectProcess,
      handleActivateProcess,
      handleLocateStep,
      currentStepActorId,
      currentStepParticipants,
    ],
  );

  return (
    <ProcessPlaybackContext.Provider value={value}>
      {children}
    </ProcessPlaybackContext.Provider>
  );
}
